import React, { useState, useEffect } from 'react';
import { Modal } from '@material-ui/core';
import { useForm, useController } from 'react-hook-form';
import { toastFlashMessage } from '@/utils';
import {
  BoxV2 as Box,
  Button,
  Flex,
  TextInput,
  MultiSelector,
} from 'portal-commons';
import { PartnerCampaign } from '../../types';
import { SelectItem } from '@/utils/types';
import { campaignController, RejectCampaignParams } from '@/apis/core';
import { CnpCampaignAction } from '../../enums';
import {
  isValidInput,
  createMaxLengthValidator,
  createRequiredValidator,
} from '../../validators';

interface Props {
  open: boolean;
  campaign: Pick<PartnerCampaign, 'uid' | 'brandUid' | 'downstreamCnpName'>;
  categories: SelectItem[];
  onClose: (type: CnpCampaignAction, reload?: boolean) => void;
}

interface FormValues {
  explanation: string;
  rejectionCategory: string[];
}

const RejectCampaignDialog: React.FC<Props> = ({
  open,
  campaign,
  categories = [],
  onClose,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: {
      explanation: '',
      rejectionCategory: [],
    },
  });

  const {
    field: { value: category, onChange: setCategory },
    fieldState: { error: categoryError },
  } = useController({
    control,
    name: 'rejectionCategory',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select a category'),
      },
    },
  });

  const {
    field: {
      value: explanation,
      onChange: setExplanation,
      onBlur: validateExplanation,
    },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Explanation'),
        isValidMaxLength: createMaxLengthValidator(2048),
        isValidInput,
      },
    },
  });

  const disabledSubmit =
    isProcessing || Object.values(errors).some((error) => !!error?.message);

  const onValidHandler = async (data: FormValues) => {
    setIsProcessing(true);

    const response = await campaignController.rejectSharedCampaign(
      campaign.uid,
      data as RejectCampaignParams
    );
    if (response) {
      toastFlashMessage(`Campaign rejected successfully`, 'success');
      onClose(CnpCampaignAction.Reject, true);
    }
    setIsProcessing(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} data-testid="rejectCampaignModal">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 525,
          minHeight: 200,
          bg: '#FFF',
          p: '24px 32px 8px',
          borderRadius: 10,
          '&:focus-visible': { outline: 'none' },
        }}
        data-testid="rejectCampaignModalContainer"
      >
        <Box
          as="h3"
          sx={{
            py: 's',
            fontSize: 'H600',
            fontWeight: 600,
            lineHeight: 'normal',
            color: 't.black80',
            textAlign: 'center',
          }}
        >
          Decline Campaign
        </Box>
        <form noValidate onSubmit={handleSubmit(onValidHandler)}>
          <Flex
            sx={{
              flexDirection: 'column',
              gap: 18,
              my: 'sx',
            }}
          >
            <Box
              as="span"
              sx={{
                fontSize: 'H100',
                color: 't.grey500',
                mb: '-10px',
                lineHeight: 'normal',
              }}
            >
              * Indicates a Required Field
            </Box>
            <TextInput readOnly label="Campaign ID" value={campaign.uid} />
            <TextInput readOnly label="Brand ID" value={campaign.brandUid} />
            <TextInput
              readOnly
              label="Downstream CNP"
              value={campaign.downstreamCnpName}
            />
            <Box sx={{ width: '100%', mt: '-4px' }}>
              <MultiSelector
                disabled={isProcessing}
                options={categories}
                data-testid="rejectCategory"
                label="Category *"
                placeholder="Category"
                value={category[0]}
                error={!!categoryError?.message}
                helperText={categoryError?.message}
                onChange={(value: string) => {
                  if (value.length) {
                    const categoryArray = value.split(',');
                    setCategory(categoryArray);
                  } else {
                    setCategory([]);
                  }
                }}
              />
            </Box>
            <TextInput
              required
              data-testid="rejectExplanation"
              label="Explanation"
              placeholder="Please enter Explanation"
              disabled={isProcessing}
              value={explanation}
              maxLength={2048}
              error={!!explanationError?.message}
              note={explanationError?.message}
              onChange={setExplanation}
              onBlur={validateExplanation}
            />
            <Flex sx={{ py: 30, gap: 18, justifyContent: 'center' }}>
              <Button
                variant="outline"
                disabled={isProcessing}
                data-testid="rejectCampaignCancelButton"
                onClick={() => onClose(CnpCampaignAction.Reject, false)}
              >
                Cancel
              </Button>
              <Button
                disabled={disabledSubmit}
                data-testid="rejectCampaignDeclineButton"
                type="submit"
              >
                Decline
              </Button>
            </Flex>
          </Flex>
        </form>
      </Box>
    </Modal>
  );
};

export default RejectCampaignDialog;
